import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import {
    getTargetTimezoneDate as utilGetTargetTimezoneDate,
    hasAvailableWorkingHours as utilHasAvailableWorkingHours,
    getUtcMidnightTimestampForDateInTimezone,
} from '../utils/timezone-helpers';
import { cn } from '../../../utils/cn';
import { MONTH_NAMES, DAY_NAMES_SHORT, MAX_LOOKAHEAD_DAYS } from '../constants';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/select';

interface FreeDay {
    date: string;
    freeSlots: Array<{ label: string; timestamp: number }>;
}

const normalizeToMidnight = (date: Date): Date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const formatDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const EMPTY_DISABLED_WEEKDAYS: number[] = [];

interface CalendarMobileProps {
    timeZone?: string;
    onDateSelect?: (timestamp: number) => void;
    initialSelectedDate?: Date;
    selectedTimestamp?: number;
    freeDays?: FreeDay[];
    disabledWeekdays?: number[];
}

const CalendarMobile: React.FC<CalendarMobileProps> = ({
    timeZone = 'Pacific/Auckland',
    onDateSelect,
    initialSelectedDate,
    selectedTimestamp,
    freeDays,
    disabledWeekdays: disabledWeekdaysProp,
}) => {
    const disabledWeekdays = disabledWeekdaysProp ?? EMPTY_DISABLED_WEEKDAYS;
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isScrolledToStart, setIsScrolledToStart] = useState(true);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
    const navigatingToPreviousMonth = useRef(false);

    const freeDateStrings = useMemo(() => {
        if (!freeDays || freeDays.length === 0) return new Set<string>();
        return new Set(
            freeDays
                .filter((day) => day.freeSlots && day.freeSlots.length > 0)
                .map((day) => day.date),
        );
    }, [freeDays]);

    const getTargetTimezoneDate = useCallback(
        (timestamp?: number): Date =>
            utilGetTargetTimezoneDate(timeZone, timestamp),
        [timeZone],
    );

    const hasAvailableWorkingHours = useCallback(
        (date: Date): boolean => utilHasAvailableWorkingHours(timeZone, date),
        [timeZone],
    );

    const isDateFree = useCallback(
        (date: Date) => freeDateStrings.has(formatDateKey(date)),
        [freeDateStrings],
    );

    const isDateDisabledByWeekday = useCallback(
        (date: Date) => disabledWeekdays.includes(date.getDay()),
        [disabledWeekdays],
    );

    const isDateSelectable = useCallback(
        (date: Date) => {
            const normalized = normalizeToMidnight(date);
            return (
                hasAvailableWorkingHours(normalized) &&
                !isDateDisabledByWeekday(normalized) &&
                isDateFree(normalized)
            );
        },
        [hasAvailableWorkingHours, isDateDisabledByWeekday, isDateFree],
    );

    const findNextSelectableDate = useCallback(
        (startDate: Date) => {
            const candidate = normalizeToMidnight(startDate);
            for (let i = 0; i < MAX_LOOKAHEAD_DAYS; i++) {
                if (isDateSelectable(candidate)) return new Date(candidate);
                candidate.setDate(candidate.getDate() + 1);
            }
            return null;
        },
        [isDateSelectable],
    );

    const emitSelection = useCallback(
        (date: Date) => {
            if (!onDateSelect) return;
            const localMidnight = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0,
            );
            const midnightInTargetTimezone =
                getUtcMidnightTimestampForDateInTimezone(
                    localMidnight,
                    timeZone,
                );
            onDateSelect(midnightInTargetTimezone);
        },
        [onDateSelect, timeZone],
    );

    const selectedDateFromTimestamp = useMemo(() => {
        if (selectedTimestamp === undefined || selectedTimestamp === null)
            return null;
        const dateInZone = getTargetTimezoneDate(selectedTimestamp);
        dateInZone.setHours(0, 0, 0, 0);
        return dateInZone;
    }, [selectedTimestamp, getTargetTimezoneDate]);

    const hasProcessedInitialSelectedTimestamp = useRef(false);
    const [currentDate, setCurrentDate] = useState(() =>
        getTargetTimezoneDate(),
    );
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        initialSelectedDate || null,
    );

    const todayInTarget = getTargetTimezoneDate();

    useEffect(() => {
        if (selectedDate) return;
        const nextDate = findNextSelectableDate(getTargetTimezoneDate());
        if (!nextDate) {
            setSelectedDate(null);
            setCurrentDate(getTargetTimezoneDate());
            return;
        }
        setCurrentDate(new Date(nextDate));
        setSelectedDate(new Date(nextDate));
        emitSelection(nextDate);
    }, [
        selectedDate,
        findNextSelectableDate,
        getTargetTimezoneDate,
        emitSelection,
    ]);

    useEffect(() => {
        if (!selectedDateFromTimestamp) return;
        if (!hasProcessedInitialSelectedTimestamp.current) {
            hasProcessedInitialSelectedTimestamp.current = true;
            return;
        }
        const nextDate = findNextSelectableDate(selectedDateFromTimestamp);
        if (!nextDate) {
            setSelectedDate(null);
            return;
        }
        let didChange = false;
        setSelectedDate((prev) => {
            if (prev?.getTime() === nextDate.getTime()) return prev;
            didChange = true;
            return new Date(nextDate);
        });
        setCurrentDate((prev) => {
            if (
                prev.getFullYear() === nextDate.getFullYear() &&
                prev.getMonth() === nextDate.getMonth()
            ) {
                return prev;
            }
            return new Date(nextDate);
        });
        if (
            didChange ||
            nextDate.getTime() !== selectedDateFromTimestamp.getTime()
        ) {
            emitSelection(nextDate);
        }
    }, [selectedDateFromTimestamp, findNextSelectableDate, emitSelection]);

    useEffect(() => {
        if (!selectedDate) return;
        if (isDateSelectable(selectedDate)) return;
        const nextDate = findNextSelectableDate(selectedDate);
        if (!nextDate) {
            setSelectedDate(null);
            return;
        }
        setSelectedDate(new Date(nextDate));
        setCurrentDate((prev) => {
            if (
                prev.getFullYear() === nextDate.getFullYear() &&
                prev.getMonth() === nextDate.getMonth()
            ) {
                return prev;
            }
            return new Date(nextDate);
        });
        emitSelection(nextDate);
    }, [selectedDate, isDateSelectable, findNextSelectableDate, emitSelection]);

    const visibleDates = useMemo(() => {
        const dates: Date[] = [];
        const start = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            1,
        );
        const end = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0,
        );
        const today = normalizeToMidnight(todayInTarget);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            if (d >= today) {
                dates.push(new Date(d));
            }
        }
        return dates;
    }, [currentDate, todayInTarget]);

    const handleDateSelect = (date: Date) => {
        if (!isDateSelectable(date)) return;
        setSelectedDate(date);
        emitSelection(date);
    };

    const scrollDates = (direction: 'left' | 'right') => {
        if (!scrollContainerRef.current) return;
        const scrollAmount = 200;
        scrollContainerRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };

    const checkScrollPosition = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        setIsScrolledToStart(scrollLeft <= 16);
        setIsScrolledToEnd(scrollLeft + clientWidth >= scrollWidth - 16);
    }, []);

    // Set up scroll event listener
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Check initial position
        checkScrollPosition();

        // Add scroll listener
        container.addEventListener('scroll', checkScrollPosition);

        return () => {
            container.removeEventListener('scroll', checkScrollPosition);
        };
    }, [checkScrollPosition]);

    // Re-check scroll position when visible dates change
    useEffect(() => {
        checkScrollPosition();
    }, [visibleDates, checkScrollPosition]);

    const canNavigateToPreviousMonth = (): boolean => {
        const previousMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            1,
        );
        const today = new Date(
            todayInTarget.getFullYear(),
            todayInTarget.getMonth(),
            1,
        );
        return previousMonth >= today;
    };

    const handlePreviousClick = () => {
        if (isScrolledToStart) {
            // Navigate to previous month if scrolled to the start
            if (!canNavigateToPreviousMonth()) return;
            navigatingToPreviousMonth.current = true;
            const previousMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() - 1,
                1,
            );
            setCurrentDate(previousMonth);
        } else {
            // Otherwise, just scroll left
            scrollDates('left');
        }
    };

    const handleNextClick = () => {
        if (isScrolledToEnd) {
            // Navigate to next month if scrolled to the end
            if (!canNavigateToNextMonth()) return;
            navigatingToPreviousMonth.current = false;
            const nextMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                1,
            );
            setCurrentDate(nextMonth);
        } else {
            // Otherwise, just scroll right
            scrollDates('right');
        }
    };

    // Scroll to beginning or end when month changes
    const currentMonthKey = useMemo(
        () => `${currentDate.getFullYear()}-${currentDate.getMonth()}`,
        [currentDate],
    );

    useEffect(() => {
        if (!scrollContainerRef.current) return;

        if (navigatingToPreviousMonth.current) {
            // Scroll to end for previous month (instant)
            scrollContainerRef.current.scrollTo({
                left: scrollContainerRef.current.scrollWidth,
                behavior: 'auto',
            });
            navigatingToPreviousMonth.current = false;
        } else {
            // Scroll to beginning for next month or month picker (smooth)
            scrollContainerRef.current.scrollTo({
                left: 0,
                behavior: 'smooth',
            });
        }

        // Check scroll position after the scroll completes
        requestAnimationFrame(() => {
            checkScrollPosition();
        });
    }, [currentMonthKey, checkScrollPosition]);

    // Auto-scroll to selected date when it changes
    useEffect(() => {
        if (!selectedDate || !scrollContainerRef.current) return;

        const scheduledScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const dateKey = formatDateKey(selectedDate);
            const selectedButton = container.querySelector<HTMLButtonElement>(
                `button[data-date="${dateKey}"]`,
            );

            if (selectedButton) {
                selectedButton.scrollIntoView({
                    block: 'nearest',
                    inline: 'center',
                    behavior: 'smooth',
                });
            }
        };

        const animationId = requestAnimationFrame(scheduledScroll);

        return () => {
            cancelAnimationFrame(animationId);
        };
    }, [selectedDate]);

    const canNavigateToNextMonth = (): boolean => {
        const nextMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            1,
        );
        const oneMonthFromNow = new Date(
            todayInTarget.getFullYear(),
            todayInTarget.getMonth() + 1,
            1,
        );
        return nextMonth <= oneMonthFromNow;
    };

    const monthOptions = useMemo(() => {
        const options: Array<{ value: string; label: string; date: Date }> = [];
        const today = todayInTarget;
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        for (let i = 0; i < 2; i++) {
            const month = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + i,
                1,
            );
            options.push({
                value: `${month.getFullYear()}-${month.getMonth()}`,
                label: `${MONTH_NAMES[month.getMonth()]} ${month.getFullYear()}`,
                date: month,
            });
        }
        return options;
    }, [todayInTarget]);

    const currentMonthValue = useMemo(
        () => `${currentDate.getFullYear()}-${currentDate.getMonth()}`,
        [currentDate],
    );

    const handleMonthChange = (yearMonth: string) => {
        const [year, month] = yearMonth.split('-').map(Number);
        if (year === undefined || month === undefined) return;
        setCurrentDate(new Date(year, month, 1));
    };

    return (
        <div className="w-full">
            <div className="mb-3 pl-4">
                <Select
                    value={currentMonthValue}
                    onValueChange={handleMonthChange}
                >
                    <SelectTrigger className="w-auto border-none bg-transparent p-0 text-sm font-semibold text-[#161717] hover:bg-transparent">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {monthOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="relative">
                <button
                    type="button"
                    onClick={handlePreviousClick}
                    aria-label="Previous week or month"
                    disabled={
                        isScrolledToStart && !canNavigateToPreviousMonth()
                    }
                    className="absolute top-1/2 left-0 z-10 flex h-[110%] w-6 -translate-y-1/2 items-center justify-center bg-white disabled:invisible"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>

                <div
                    ref={scrollContainerRef}
                    className="flex gap-1 overflow-x-auto"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {visibleDates.map((date) => {
                        const isSelected =
                            selectedDate &&
                            date.getDate() === selectedDate.getDate() &&
                            date.getMonth() === selectedDate.getMonth() &&
                            date.getFullYear() === selectedDate.getFullYear();
                        const isDisabled = !isDateSelectable(date);
                        const isPast =
                            date < normalizeToMidnight(todayInTarget);

                        return (
                            <button
                                key={formatDateKey(date)}
                                type="button"
                                onClick={() => handleDateSelect(date)}
                                disabled={isDisabled}
                                data-date={formatDateKey(date)}
                                className={cn(
                                    'flex min-w-[56px] flex-col items-center gap-1 rounded-[8px] border px-3 py-2 text-sm transition-colors',
                                    isSelected &&
                                        'border-brick-100 bg-brick-100 text-black',
                                    (isDisabled || isPast) &&
                                        'cursor-not-allowed bg-[#F6F6F6] text-[#BDBDBD]',
                                    !isSelected &&
                                        !isDisabled &&
                                        !isPast &&
                                        'border-[#D8D8D8] text-[#888888] hover:border-[#C0C0C0]',
                                )}
                            >
                                <span className="text-4">
                                    {DAY_NAMES_SHORT[date.getDay()]}
                                </span>
                                <span
                                    className={cn(
                                        isDisabled || isPast
                                            ? 'text-[#BDBDBD]'
                                            : 'text-black',
                                    )}
                                >
                                    {date.getDate()}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={handleNextClick}
                    aria-label="Next week or month"
                    disabled={isScrolledToEnd && !canNavigateToNextMonth()}
                    className="absolute top-1/2 right-0 z-10 flex h-[110%] w-6 -translate-y-1/2 items-center justify-center bg-white disabled:invisible"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

export { CalendarMobile };
